<template>
  <v-row class="mx-1 mt-1" align="center">
    <v-col cols="12">
      <v-row dense align="center">
        <v-col cols="auto">
          <v-select
          v-model="selectedFiscalYearId"
          :items="fiscalYearItems"
          :label="$t('fiscalYear')"
          :menu-props="{ bottom: true, offsetY: true }"
          id="gl-reconciliation-fiscal-year-select"
          style="width: 260px"
          outlined
          dense
          hide-details/>
        </v-col>
        <v-col cols="auto">
          <v-select
          v-model="selectedPayPeriodIds"
          :items="payPeriodItems"
          :label="$t('payPeriods')"
          :menu-props="{ bottom: true, offsetY: true }"
          id="gl-reconciliation-pay-period-select"
          style="width: 340px"
          multiple
          outlined
          dense
          hide-details>
            <template #selection="{ item, index }">
              <span v-if="index === 0">{{ item.text }}</span>
              <span v-else-if="index === 1" class="grey--text caption ml-1">(+{{ $t('nMore', { n: selectedPayPeriodIds.length - 1 }) }})</span>
            </template>
          </v-select>
        </v-col>
        <v-col cols="auto" v-if="showExportBatchFilter">
          <v-select
          v-model="selectedExportBatchIds"
          :items="batchItems"
          :label="$t('exportBatches')"
          :menu-props="{ bottom: true, offsetY: true }"
          style="width: 300px"
          multiple
          outlined
          dense
          hide-details>
            <template #prepend-item>
              <v-list-item ripple @mousedown.prevent @click="toggleAllBatches">
                <v-list-item-action>
                  <v-icon color="primary">{{ selectAllBatchesIcon }}</v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>{{ $t('selectAll') }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider/>
            </template>
            <template #selection="{ index }">
              <span v-if="index === 0" class="text-truncate" style="max-width: 240px">{{ batchSelectionText }}</span>
            </template>
          </v-select>
        </v-col>
        <v-col cols="auto">
          <v-select
          v-model="selectedEntityId"
          :items="entityItems"
          :label="$t('businessEntity')"
          :menu-props="{ bottom: true, offsetY: true }"
          style="width: 260px"
          outlined
          dense
          hide-details>
            <template #selection="{ item }">
              <span class="text-truncate" style="max-width: 200px">{{ item.text }}</span>
            </template>
          </v-select>
        </v-col>
        <v-col cols="auto">
          <v-text-field
          v-model="search"
          :label="$t('searchGlCodeOrOffset')"
          append-icon="mdi-magnify"
          style="width: 260px"
          outlined
          dense
          clearable
          hide-details/>
        </v-col>
        <v-spacer/>
        <v-col cols="auto">
          <Icon
          @icon-clicked="loader.load(true)"
          icon="mdi-refresh"
          :small="false"
          :tooltipText="$t('refresh')"/>
        </v-col>
        <v-col cols="auto">
          <v-btn
          class="primary"
          :disabled="downloadDisabled"
          :loading="pdfLoading"
          @click="downloadPdf">
            {{ $t('downloadPDF') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-col>
    <v-col cols="12" class="py-0">
      <v-row dense align="center">
        <v-col cols="auto" class="caption">
          {{ glPairsText }}
        </v-col>
        <v-spacer/>
        <v-col cols="auto">
          <span style="color: black; font-size: 20px">
            {{ grandTotalText }}
          </span>
        </v-col>
      </v-row>
    </v-col>
    <v-col cols="12" class="pa-0" style="min-height: 4px">
      <v-progress-linear v-if="loader.loading" color="primary" indeterminate/>
    </v-col>
    <v-col cols="12">
      <v-row v-if="rebuilding" class="my-12" justify="center">
        <span class="subtitle-1 black--text">{{ $t('exportInProgressPleaseRefresh') }}</span>
      </v-row>
      <v-row v-else-if="selectedPayPeriodIds.length === 0" class="my-12" justify="center">
        <span class="subtitle-1 black--text">{{ $t('selectPayPeriodsToBegin') }}</span>
      </v-row>
      <v-row v-else-if="!loader.loading && visibleGroups.length === 0" class="my-12" justify="center">
        <span class="subtitle-1 black--text">{{ $t('noGlActivityForSelection') }}</span>
      </v-row>
      <v-row v-else>
        <v-expansion-panels accordion multiple v-model="openPanels" tile class="pa-1">
          <v-expansion-panel v-for="(group, index) in visibleGroups" :key="`gl-group-${index}`">
            <v-expansion-panel-header>
              <v-row dense>
                <v-col cols="auto">
                  <div>{{ groupTitle(group) }}</div>
                  <div class="caption">{{ group.businessEntityName }}</div>
                </v-col>
              </v-row>
              <v-spacer/>
              <template #actions>
                <table class="gl-totals">
                  <tr>
                    <td>{{ $t('amount') }}:</td>
                    <td class="text-right">{{ formatMoney(group.amount) }}</td>
                  </tr>
                  <tr>
                    <td>{{ $t('netTons') }}:</td>
                    <td class="text-right">{{ numberWithCommas(group.netTons, 3) }}</td>
                  </tr>
                  <tr>
                    <td>{{ $t('netMbf') }}:</td>
                    <td class="text-right">{{ numberWithCommas(group.netMbf, 3) }}</td>
                  </tr>
                </table>
              </template>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="lg_expansion-panel_wrapper">
                <DataTable
                :items="group.accounts"
                :headers="headers"
                hideHeader
                propDense/>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { ResourceLoader } from '@/utils/ResourceLoader'
import { glReconciliationHeaders } from '@/headers/GLReconciliation.js'
import { formatMoney, numberWithCommas } from '@/utils/NumericMutations.js'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { fileNameForString } from '@/utils/pdf-formatters/GenericSettlementsFunctions.js'
import { createGLReconciliationPDF } from '@/utils/pdf-formatters/GLReconciliationReport.js'
import { FiscalYearStatus, PayPeriodStatus, SettlementArchiveStatus } from '@/utils/Enumerations.js'

const POLL_INTERVAL_MS = 2500
const POLL_LIMIT = 12

const inclusiveEndDate = (endDate) => {
  const date = new Date(endDate)
  date.setDate(date.getDate() - 1)
  return date
}

export default {
  name: 'GLReconciliation',

  components: {
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  data () {
    return {
      selectedFiscalYearId: null,
      selectedPayPeriodIds: [],
      selectedExportBatchIds: [],
      selectedEntityId: null,
      payPeriods: [],
      batchesByPeriodId: {},
      search: '',
      datasets: {},
      groups: [],
      rebuilding: false,
      rebuildingPayPeriodIds: [],
      pdfLoading: false,
      pollCount: 0,
      pollingHandle: undefined,
      openPanels: [],
      loader: ResourceLoader.empty
    }
  },

  async created () {
    this.loader = new ResourceLoader(async (force = false) => {
      await this.load(force)
    })

    await this.fetchFiscalYears()
    this.selectedFiscalYearId = this.defaultFiscalYearId(this.activeFiscalYears)
  },

  beforeDestroy () {
    this.stopPolling()
  },

  computed: {
    ...mapGetters('fiscal-year', ['allFiscalYears']),

    activeFiscalYears () {
      return this.allFiscalYears
        .filter(fy => fy.status !== FiscalYearStatus.Inactive.value)
        .sort((a, b) => Date.parse(b.startDate) - Date.parse(a.startDate))
    },

    fiscalYearItems () {
      return this.activeFiscalYears.map(fy => ({ text: this.periodLabel(fy), value: fy.fiscalYearId }))
    },

    payPeriodItems () {
      return [...this.payPeriods]
        .filter(pp => pp.status !== PayPeriodStatus.Inactive.value)
        .sort((a, b) => Date.parse(b.startDate) - Date.parse(a.startDate))
        .map(pp => ({ text: this.periodLabel(pp), value: pp.payPeriodId }))
    },

    showExportBatchFilter () {
      return this.selectedPayPeriods.length > 0 && this.allBatchIds.length > 0
    },

    batchItems () {
      const items = []
      for (const period of [...this.selectedPayPeriods].reverse()) {
        const batches = this.batchesByPeriodId[period.payPeriodId] ?? []
        if (batches.length === 0) continue
        if (this.selectedPayPeriods.length > 1) items.push({ header: this.periodLabel(period) })
        items.push(...batches.map(eb => ({ text: this.exportBatchLabel(eb), value: eb.exportBatchId })))
      }
      return items
    },

    allBatchIds () {
      return this.selectedPayPeriods.flatMap(pp => (this.batchesByPeriodId[pp.payPeriodId] ?? []).map(eb => eb.exportBatchId))
    },

    allBatchesSelected () {
      return this.allBatchIds.length > 0 && this.selectedExportBatchIds.length === this.allBatchIds.length
    },

    selectAllBatchesIcon () {
      if (this.allBatchesSelected) return 'mdi-checkbox-marked'
      return this.selectedExportBatchIds.length > 0 ? 'mdi-minus-box' : 'mdi-checkbox-blank-outline'
    },

    batchSelectionText () {
      if (this.allBatchesSelected) return this.$t('allExportBatches')
      if (this.selectedExportBatchIds.length === 1) {
        const found = this.findBatch(this.selectedExportBatchIds[0])
        if (found) return this.exportBatchLabel(found.exportBatch)
      }
      return `${this.selectedExportBatchIds.length} ${this.$t('selected')}`
    },

    entityItems () {
      const entities = new Map()
      for (const group of this.groups) {
        if (!entities.has(group.businessEntityId)) entities.set(group.businessEntityId, group.businessEntityName)
      }

      return [
        { text: this.$t('allBusinessEntities'), value: null },
        ...[...entities.entries()]
          .map(([value, text]) => ({ text, value }))
          .sort((a, b) => a.text.localeCompare(b.text))
      ]
    },

    visibleGroups () {
      const entityGroups = this.selectedEntityId === null
        ? this.groups
        : this.groups.filter(g => g.businessEntityId === this.selectedEntityId)

      const term = (this.search ?? '').trim().toLowerCase()
      return term === ''
        ? entityGroups
        : entityGroups.filter(g =>
          (g.glCode ?? '').toLowerCase().includes(term) ||
          (g.glOffset ?? '').toLowerCase().includes(term))
    },

    glPairsText () {
      return `${this.visibleGroups.length} ${this.$t('glPairs')}`
    },

    grandTotalText () {
      const total = this.visibleGroups.reduce((sum, group) => sum + group.amount, 0)
      return `${this.$t('grandTotal')}: ${formatMoney(total)}`
    },

    headers () {
      return glReconciliationHeaders()
    },

    selectedFiscalYear () {
      return this.allFiscalYears.find(fy => fy.fiscalYearId === this.selectedFiscalYearId)
    },

    selectedPayPeriods () {
      return this.selectedPayPeriodIds
        .map(id => this.payPeriods.find(pp => pp.payPeriodId === id))
        .filter(pp => pp !== undefined)
        .sort((a, b) => Date.parse(a.startDate) - Date.parse(b.startDate))
    },

    downloadDisabled () {
      return this.loader.loading ||
        this.rebuilding ||
        this.pdfLoading ||
        this.selectedPayPeriodIds.length === 0 ||
        (this.allBatchIds.length > 0 && this.selectedExportBatchIds.length === 0)
    }
  },

  watch: {
    selectedFiscalYearId (val) {
      this.selectedPayPeriodIds = []
      this.payPeriods = []
      this.batchesByPeriodId = {}
      if (val === null || val === undefined) return
      this.loadPayPeriods(val)
    },

    // Selection changes reload through the selectedExportBatchIds watcher: assigning the
    // batch selection (even an identical-content array) is a new reference, so it always fires.
    async selectedPayPeriodIds (val) {
      this.stopPolling()
      await this.ensureBatchesLoaded(val)
      if (val !== this.selectedPayPeriodIds) return
      this.selectedExportBatchIds = this.allBatchIds
    },

    selectedExportBatchIds () {
      this.stopPolling()
      this.loader.load()
    },

    visibleGroups (val) {
      this.openPanels = val.length === 1 ? [0] : []
    }
  },

  methods: {
    ...mapActions('fiscal-year', ['fetchFiscalYears']),
    ...mapActions('pay-period', ['fetchPayPeriodsNoMutation']),
    ...mapActions('export-batch', ['fetchExportBatchesNoMutation']),
    ...mapActions('settlements', ['fetchGLReconciliation', 'downloadGLReconciliationPdf']),
    formatMoney,
    numberWithCommas,

    defaultFiscalYearId (fiscalYears) {
      const now = Date.now()
      const current = fiscalYears.find(fy => Date.parse(fy.startDate) <= now && now < Date.parse(fy.endDate))
      return (current ?? fiscalYears[0])?.fiscalYearId ?? null
    },

    async loadPayPeriods (fiscalYearId) {
      const payPeriods = await this.fetchPayPeriodsNoMutation(fiscalYearId)
      if (fiscalYearId !== this.selectedFiscalYearId) return
      this.payPeriods = payPeriods
    },

    periodLabel ({ startDate, endDate }) {
      return `${utcToLocalDate(startDate)} - ${utcToLocalDate(inclusiveEndDate(endDate))}`
    },

    exportBatchLabel (exportBatch) {
      return exportBatch.label === '' || exportBatch.label === undefined
        ? utcToLocalDate(exportBatch.exportDate)
        : exportBatch.label
    },

    groupTitle (group) {
      return this.addNameWithCode(group.glCode, group.glOffset)
    },

    async ensureBatchesLoaded (payPeriodIds, force = false) {
      await Promise.all(payPeriodIds.map(async payPeriodId => {
        if (!force && this.batchesByPeriodId[payPeriodId] !== undefined) return
        const batches = await this.fetchExportBatchesNoMutation(payPeriodId)
        this.$set(this.batchesByPeriodId, payPeriodId, batches.filter(eb => eb.exportBatchId !== undefined))
      }))
    },

    toggleAllBatches () {
      this.selectedExportBatchIds = this.allBatchesSelected ? [] : [...this.allBatchIds]
    },

    findBatch (exportBatchId) {
      for (const payPeriod of this.selectedPayPeriods) {
        const exportBatch = (this.batchesByPeriodId[payPeriod.payPeriodId] ?? []).find(eb => eb.exportBatchId === exportBatchId)
        if (exportBatch) return { payPeriod, exportBatch }
      }
      return undefined
    },

    plannedFetches (periods) {
      if (this.selectedExportBatchIds.length === 0 && this.allBatchIds.length > 0) return []

      const selectedSet = new Set(this.selectedExportBatchIds)
      const fetches = []

      for (const period of periods) {
        const batches = this.batchesByPeriodId[period.payPeriodId] ?? []
        const selected = batches.filter(eb => selectedSet.has(eb.exportBatchId))

        // The period data set covers every batch (and a batchless period's live data) in one blob read.
        if (batches.length === 0 || selected.length === batches.length) {
          fetches.push({ payPeriodId: period.payPeriodId, exportBatchId: null })
        } else {
          selected.forEach(eb => fetches.push({ payPeriodId: period.payPeriodId, exportBatchId: eb.exportBatchId }))
        }
      }

      return fetches
    },

    async load (force = false) {
      const periods = this.selectedPayPeriods
      if (periods.length === 0) {
        this.groups = []
        this.rebuilding = false
        return
      }

      await this.ensureBatchesLoaded(periods.map(pp => pp.payPeriodId))
      const fetches = this.plannedFetches(periods)
      if (fetches.length === 0) {
        this.groups = []
        this.rebuilding = false
        return
      }

      const rebuildingPayPeriodIds = []
      const reports = []

      await Promise.all(fetches.map(async ({ payPeriodId, exportBatchId }) => {
        const key = `${payPeriodId}:${exportBatchId ?? 'all'}`
        if (!force && this.datasets[key] !== undefined) {
          reports.push(this.datasets[key])
          return
        }

        try {
          const report = await this.fetchGLReconciliation({ payPeriodId, exportBatchId })
          this.$set(this.datasets, key, report)
          reports.push(report)
        } catch (e) {
          const code = e?.response?.data?.code
          if (code !== 'RSTRE' && code !== 'BLOB3') throw e
          if (!rebuildingPayPeriodIds.includes(payPeriodId)) rebuildingPayPeriodIds.push(payPeriodId)
        }
      }))

      if (rebuildingPayPeriodIds.length > 0) {
        this.groups = []
        this.rebuildingPayPeriodIds = rebuildingPayPeriodIds
        this.rebuilding = true
        this.startPolling()
        return
      }

      this.rebuildingPayPeriodIds = []
      this.rebuilding = false
      this.groups = this.mergeReports(reports)
      if (this.selectedEntityId !== null && !this.entityItems.some(e => e.value === this.selectedEntityId)) {
        this.selectedEntityId = null
      }
    },

    mergeReports (reports) {
      const merged = new Map()

      for (const report of reports) {
        for (const group of report?.groups ?? []) {
          const key = `${group.businessEntityId}|${group.glCode}|${group.glOffset}`
          let entry = merged.get(key)
          if (entry === undefined) {
            entry = {
              businessEntityId: group.businessEntityId,
              businessEntityName: group.businessEntityName,
              businessEntityExportCode: group.businessEntityExportCode,
              glCode: group.glCode,
              glOffset: group.glOffset,
              amount: 0,
              netTons: 0,
              netMbf: 0,
              accountsById: new Map()
            }
            merged.set(key, entry)
          }

          // A source belongs to exactly one export batch, so periods never double count the same tons/MBF.
          entry.amount += group.amount
          entry.netTons += group.netTons
          entry.netMbf += group.netMbf

          for (const account of group.accounts ?? []) {
            const existing = entry.accountsById.get(account.accountId)
            if (existing === undefined) {
              entry.accountsById.set(account.accountId, { ...account })
              continue
            }

            existing.amount += account.amount
            existing.netTons += account.netTons
            existing.netMbf += account.netMbf
          }
        }
      }

      return [...merged.values()]
        .map(({ accountsById, ...group }) => ({
          ...group,
          accounts: [...accountsById.values()].sort((a, b) => (a.accountName ?? '').localeCompare(b.accountName ?? ''))
        }))
        .sort((a, b) =>
          (a.businessEntityName ?? '').localeCompare(b.businessEntityName ?? '') ||
          (a.glCode ?? '').localeCompare(b.glCode ?? '') ||
          (a.glOffset ?? '').localeCompare(b.glOffset ?? ''))
    },

    batchIsRebuilding (exportBatch) {
      return exportBatch.settlementArchiveStatus === SettlementArchiveStatus.Pending.value ||
        exportBatch.settlementArchiveStatus === SettlementArchiveStatus.Upgrade.value
    },

    async rebuildStillUnderway () {
      await this.ensureBatchesLoaded(this.rebuildingPayPeriodIds, true)
      const selectedSet = new Set(this.selectedExportBatchIds)

      for (const payPeriodId of this.rebuildingPayPeriodIds) {
        const batches = this.batchesByPeriodId[payPeriodId] ?? []
        const relevant = batches.filter(eb => selectedSet.size === 0 || selectedSet.has(eb.exportBatchId))
        if (relevant.some(this.batchIsRebuilding)) return true
      }

      return false
    },

    startPolling () {
      if (this.pollingHandle !== undefined) return

      this.pollCount = 0
      this.pollingHandle = setInterval(async () => {
        this.pollCount++
        if (this.pollCount > POLL_LIMIT) {
          this.stopPolling()
          return
        }

        if (await this.rebuildStillUnderway()) return

        this.stopPolling()
        this.loader.load(true)
      }, POLL_INTERVAL_MS)
    },

    stopPolling () {
      if (this.pollingHandle === undefined) return
      clearInterval(this.pollingHandle)
      this.pollingHandle = undefined
    },

    async downloadPdf () {
      const periods = this.selectedPayPeriods
      if (periods.length === 0) return

      this.pdfLoading = true
      try {
        if (periods.length === 1 && (this.allBatchesSelected || this.allBatchIds.length === 0)) {
          await this.downloadArchivedPdf(periods[0], null)
          return
        }

        if (this.selectedExportBatchIds.length === 1) {
          const found = this.findBatch(this.selectedExportBatchIds[0])
          if (found !== undefined) {
            await this.downloadArchivedPdf(found.payPeriod, found.exportBatch)
            return
          }
        }

        const footerText = this.allBatchesSelected
          ? `${periods.length} Pay Periods`
          : `${this.selectedExportBatchIds.length} Export Batches`
        const suffix = this.allBatchesSelected
          ? `${periods.length}PayPeriods`
          : `${this.selectedExportBatchIds.length}ExportBatches`

        await createGLReconciliationPDF({
          groups: this.groups,
          scopeLine: this.pdfScopeLine(),
          footerText,
          filename: fileNameForString(`GLReconciliation_${this.periodLabel(this.selectedFiscalYear)}_${suffix}`, 'pdf')
        })
      } finally {
        this.pdfLoading = false
      }
    },

    async downloadArchivedPdf (payPeriod, exportBatch) {
      const filename = exportBatch
        ? fileNameForString(`GLReconciliation_${this.exportBatchLabel(exportBatch)}`, 'pdf')
        : fileNameForString(`GLReconciliation_PayPeriod_${utcToLocalDate(payPeriod.startDate)}-${utcToLocalDate(payPeriod.endDate)}`, 'pdf')

      await this.downloadGLReconciliationPdf({
        payPeriod,
        exportBatch,
        filename
      })
    },

    pdfScopeLine () {
      const periods = this.selectedPayPeriods
      const start = utcToLocalDate(periods[0].startDate)
      const end = utcToLocalDate(inclusiveEndDate(periods[periods.length - 1].endDate))
      const fiscalYear = this.selectedFiscalYear ? `Fiscal Year: ${this.periodLabel(this.selectedFiscalYear)} · ` : ''
      const batches = this.allBatchesSelected || this.allBatchIds.length === 0
        ? ''
        : ` · ${this.selectedExportBatchIds.length} of ${this.allBatchIds.length} export batches`
      return `${fiscalYear}Pay Periods: ${start} - ${end} (${periods.length} periods)${batches}`
    }
  }
}
</script>

<style scoped>
.gl-totals {
  font-size: 16px;
}

.gl-totals td {
  min-width: 90px;
}

.gl-totals td:first-child {
  text-align: right;
  padding-right: 8px;
  color: rgba(0, 0, 0, 0.6);
}
</style>
